import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(null);

const themes = {
  softGreen: {
    name: 'Soft Green',
    bg: '#F0FDF4',
    card: '#FFFFFF',
    text: '#111827',
    primary: '#16A34A',
    border: '#E5E7EB',
    textMuted: '#6B7280',
    cardHover: '#F0FDF4',
    inputBg: '#F9FAFB',
    bgDeep: '#ECFDF5',
    isDark: false,
  },
  deepBlue: {
    name: 'Deep Blue',
    bg: '#0F172A',
    card: '#1E293B',
    text: '#F8FAFC',
    primary: '#3B82F6',
    border: '#334155',
    textMuted: '#94A3B8',
    cardHover: '#283548',
    inputBg: '#141C2E',
    bgDeep: '#0C1222',
    isDark: true,
  },
  orange: {
    name: 'Orange',
    bg: '#FFF7ED',
    card: '#FFFFFF',
    text: '#1F2937',
    primary: '#F97316',
    border: '#FED7AA',
    textMuted: '#6B7280',
    cardHover: '#FEF3E2',
    inputBg: '#FFF7ED',
    bgDeep: '#FFEDD5',
    isDark: false,
  },
  purple: {
    name: 'Purple',
    bg: '#FAF5FF',
    card: '#FFFFFF',
    text: '#1F2937',
    primary: '#8B5CF6',
    border: '#E9D5FF',
    textMuted: '#6B7280',
    cardHover: '#F5F3FF',
    inputBg: '#FAF5FF',
    bgDeep: '#EDE9FE',
    isDark: false,
  },
  light: {
    name: 'Light',
    bg: '#F9FAFB',
    card: '#FFFFFF',
    text: '#111827',
    primary: '#10B981',
    border: '#D1D5DB',
    textMuted: '#6B7280',
    cardHover: '#F3F4F6',
    inputBg: '#F9FAFB',
    bgDeep: '#E5E7EB',
    isDark: false,
  },
  darkGreen: {
    name: 'Dark Green',
    bg: '#0A1F14',
    card: '#122B1E',
    text: '#E2E8F0',
    primary: '#22C55E',
    border: '#2D6B4A',
    textMuted: '#7BAF96',
    cardHover: '#1A3D2A',
    inputBg: '#0D2518',
    bgDeep: '#0D2518',
    isDark: true,
  },
  black: {
    name: 'Black',
    bg: '#0A0A0A',
    card: '#171717',
    text: '#F5F5F5',
    primary: '#FFFFFF',
    border: '#333333',
    textMuted: '#A3A3A3',
    cardHover: '#1F1F1F',
    inputBg: '#0F0F0F',
    bgDeep: '#050505',
    isDark: true,
  },
  red: {
    name: 'Red',
    bg: '#1F0A0A',
    card: '#2A1414',
    text: '#FEE2E2',
    primary: '#EF4444',
    border: '#7F1D1D',
    textMuted: '#FCA5A5',
    cardHover: '#351C1C',
    inputBg: '#1A0E0E',
    bgDeep: '#180808',
    isDark: true,
  },
  teal: {
    name: 'Teal',
    bg: '#0A1F1F',
    card: '#142929',
    text: '#CCFBF1',
    primary: '#14B8A6',
    border: '#115E59',
    textMuted: '#5EEAD4',
    cardHover: '#1C3333',
    inputBg: '#0E1A1A',
    bgDeep: '#081818',
    isDark: true,
  },
  grey: {
    name: 'Grey',
    bg: '#141414',
    card: '#1E1E1E',
    text: '#E5E5E5',
    primary: '#A0A0A0',
    border: '#404040',
    textMuted: '#9CA3AF',
    cardHover: '#2A2A2A',
    inputBg: '#181818',
    bgDeep: '#0F0F0F',
    isDark: true,
  },
  neon: {
    name: 'Neon',
    bg: '#0A0F0A',
    card: '#111A11',
    text: '#D1FAE5',
    primary: '#39FF14',
    border: '#1A4D1A',
    textMuted: '#6EE7B7',
    cardHover: '#1A2A1A',
    inputBg: '#0D120D',
    bgDeep: '#080C08',
    isDark: true,
  },
  gold: {
    name: 'Gold',
    bg: '#1A170A',
    card: '#252010',
    text: '#FEF3C7',
    primary: '#EAB308',
    border: '#854D0E',
    textMuted: '#FCD34D',
    cardHover: '#302A18',
    inputBg: '#1E1A0E',
    bgDeep: '#141108',
    isDark: true,
  },
  pink: {
    name: 'Pink',
    bg: '#1F0A18',
    card: '#2A1422',
    text: '#FCE7F3',
    primary: '#EC4899',
    border: '#9D174D',
    textMuted: '#F9A8D4',
    cardHover: '#351C2C',
    inputBg: '#1A0E16',
    bgDeep: '#180812',
    isDark: true,
  },
  cyan: {
    name: 'Cyan',
    bg: '#0A1A1F',
    card: '#142229',
    text: '#CFFAFE',
    primary: '#06B6D4',
    border: '#155E75',
    textMuted: '#67E8F9',
    cardHover: '#1C2C33',
    inputBg: '#0E1619',
    bgDeep: '#081214',
    isDark: true,
  },
};

function applyTheme(themeName) {
  const t = themes[themeName] || themes.softGreen;
  const root = document.documentElement;

  // Core vars
  root.style.setProperty('--bg', t.bg);
  root.style.setProperty('--card', t.card);
  root.style.setProperty('--text', t.text);
  root.style.setProperty('--primary', t.primary);

  // Backward compat: --dm-* vars
  root.style.setProperty('--dm-bg-primary', t.bg);
  root.style.setProperty('--dm-bg-card', t.card);
  root.style.setProperty('--dm-bg-card-hover', t.cardHover);
  root.style.setProperty('--dm-bg-deep', t.bgDeep);
  root.style.setProperty('--dm-border', t.border);
  root.style.setProperty('--dm-accent', t.primary);
  root.style.setProperty('--dm-accent-hover', `${t.primary}22`);
  root.style.setProperty('--dm-text-muted', t.textMuted);
  root.style.setProperty('--dm-input-bg', t.inputBg);
  root.style.setProperty('--dm-scroll-thumb', t.border);
  root.style.setProperty('--dm-scroll-thumb-hover', t.primary);

  // Body background — solid color, no gradient
  document.body.style.background = t.bg;
  document.body.style.backgroundAttachment = 'fixed';
  document.body.style.color = t.text;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    return localStorage.getItem('dm_theme') || 'softGreen';
  });

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((newTheme) => {
    if (themes[newTheme]) {
      setThemeState(newTheme);
      localStorage.setItem('dm_theme', newTheme);
    }
  }, []);

  const currentTheme = themes[theme] || themes.softGreen;

  return (
    <ThemeContext.Provider value={{ theme, setTheme, currentTheme, themes }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
